// main.js
import React, { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { PAGE_REDIRECTS, VIEW_REDIRECTS } from './pageRoutes';
import { MsalProvider } from '@azure/msal-react';
import { msalInstance } from './authConfig';
import { ThemeProvider } from './ThemeContext';
import { AuthProvider, useAuth } from './AuthContext';
import Login from './Login';
import Sidebar from './Sidebar';
import { EMBED, installEmbedBridge } from './embedMode';
import Home from './Home';
import { LoadingBlock } from './Spinner';
// Every page's CSS, eagerly and in order — see the file for why it must not travel with the page.
import './pageStyles';
import './App.css';
// Loaded after App.css and every page stylesheet so the phone layout wins ties.
import './mobile.css';

/* Pages are fetched when first opened. Every page used to be a static import, so the first visit —
 * the login screen included — downloaded and parsed every report, the tracking playground and the
 * video tooling before anything drew: one 5 MB script.
 *
 * A deploy replaces the hashed chunk files, so a tab opened before it asks for a chunk that no longer
 * exists. That is answered with one reload (which fetches the new index and its chunks), remembered
 * for the session so a chunk that is genuinely broken shows its error instead of reloading forever. */
const CHUNK_RELOAD_KEY = 'ettifaq_chunk_reload';
function lazyPage(load) {
  return lazy(() => load()
    .then((m) => { try { sessionStorage.removeItem(CHUNK_RELOAD_KEY); } catch { /* private mode */ } return m; })
    .catch((err) => {
      let reloaded = true;
      try { reloaded = !!sessionStorage.getItem(CHUNK_RELOAD_KEY); } catch { /* private mode */ }
      if (!reloaded) {
        try { sessionStorage.setItem(CHUNK_RELOAD_KEY, '1'); } catch { /* private mode */ }
        window.location.reload();
        return new Promise(() => {});
      }
      throw err;
    }));
}

const DayToDaySummaries = lazyPage(() => import('./DayToDaySummaries'));
const SquadDepth = lazyPage(() => import('./SquadDepth'));
const OppositionPhysicals = lazyPage(() => import('./OppositionPhysicals'));
const TrainingDrillCalculator = lazyPage(() => import('./TrainingDrillCalculator'));
const IndividualPlayerLoadTrackingReport = lazyPage(() => import('./IndividualPlayerLoadTrackingReport'));
const LoadBudgetChronicLoadOverview = lazyPage(() => import('./LoadBudgetChronicLoadOverview'));
const WellnessLongitudinalTracking = lazyPage(() => import('./WellnessLongitudinalTracking'));
const WellnessLateness = lazyPage(() => import('./WellnessLateness'));
const Rehab = lazyPage(() => import('./Rehab'));
const Availability = lazyPage(() => import('./MatchAvailability'));
const Travel = lazyPage(() => import('./Travel'));
const Testing = lazyPage(() => import('./Testing'));
const NutritionPage = lazyPage(() => import('./NutritionPage'));
const Conditioning = lazyPage(() => import('./Conditioning'));
const MatchPerformance = lazyPage(() => import('./MatchPerformance'));
const CongestionManagement = lazyPage(() => import('./CongestionManagement'));
const HeartRateReport = lazyPage(() => import('./HeartRate'));
const GameModelReview = lazyPage(() => import('./GameModelReview'));
const Live = lazyPage(() => import('./Live'));
const TacticalXml = lazyPage(() => import('./TacticalXml'));
const StyleOfPlay = lazyPage(() => import('./StyleOfPlay'));
const DataPullPerformance = lazyPage(() => import('./DataPullPerformance'));
const DataPullFootball = lazyPage(() => import('./DataPullFootball'));
const TeamAnalysis = lazyPage(() => import('./TeamAnalysis'));
const OppositionPreview = lazyPage(() => import('./OppositionPreview'));
const TrainingFocus = lazyPage(() => import('./TrainingFocus'));
const PlaygroundPage = lazyPage(() => import('./PlaygroundPage'));
const SetPieceSummary = lazyPage(() => import('./SetPieceSummary'));
const DataEntry = lazyPage(() => import('./DataEntry'));
const TrainingSessionInformation = lazyPage(() => import('./TrainingSessionInformation'));
const MatchNotes = lazyPage(() => import('./MatchNotes'));
const IDP = lazyPage(() => import('./IDP'));
const GameModel = lazyPage(() => import('./GameModel'));
const ManualDataEntry = lazyPage(() => import('./ManualDataEntry'));
const InjuryIllness = lazyPage(() => import('./InjuryIllness'));
const Permissions = lazyPage(() => import('./Permissions'));
const Tableau = lazyPage(() => import('./Tableau'));
const ScoreboardCreator = lazyPage(() => import('./ScoreboardCreator'));
const VideoSizeReducer = lazyPage(() => import('./VideoSizeReducer'));
const LineUpCreator = lazyPage(() => import('./LineUpCreator'));
const Schedule = lazyPage(() => import('./Schedule'));
const Chat = lazyPage(() => import('./Chat'));
const MatchVideoUploader = lazyPage(() => import('./MatchVideoUploader'));
const StorageTesting = lazyPage(() => import('./StorageTesting'));
const SetPieces = lazyPage(() => import('./SetPieces'));
const SetPiecePositioning = lazyPage(() => import('./SetPiecePositioning'));
const MetricMaker = lazyPage(() => import('./MetricMaker'));
const MetricExplorer = lazyPage(() => import('./MetricExplorer'));
const OverTimeMetricExplorer = lazyPage(() => import('./OverTimeMetricExplorer'));
const GameModelSimilarity = lazyPage(() => import('./GameModelSimilarity'));
const SquadRating = lazyPage(() => import('./SquadRating'));
const SquadValuations = lazyPage(() => import('./SquadValuations'));
const GoalsAnalysis = lazyPage(() => import('./GoalsAnalysis'));
const PackingZones = lazyPage(() => import('./PackingZones'));
const Movement = lazyPage(() => import('./Movement'));
const ExploitingSpace = lazyPage(() => import('./ExploitingSpace'));
const ScoutingRecruitment = lazyPage(() => import('./ScoutingRecruitment'));
const SecondBalls = lazyPage(() => import('./SecondBalls'));
const PlayerSynergies = lazyPage(() => import('./PlayerSynergies'));
const CoachingPostMatchReport = lazyPage(() => import('./CoachingPostMatchReport'));
const BallInPlay = lazyPage(() => import('./BallInPlay'));

function PageLoading() {
  return (
    <div style={{ height: '100%', minHeight: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <LoadingBlock text="Opening…" />
    </div>
  );
}

// Blocks direct-URL access to a page the signed-in user isn't allowed to open.
function NoAccess() {
  return (
    <div style={{
      height: '100%', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', textAlign: 'center',
      color: 'var(--text-tertiary)', gap: 8, padding: 24,
    }}>
      <div style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-secondary)' }}>
        No access
      </div>
      <div style={{ fontSize: '0.8rem', maxWidth: 340 }}>
        You don't have permission to view this page. Contact an administrator if you
        need access.
      </div>
    </div>
  );
}

// Gate the routed content by the current path's access rule.
function AccessGate({ children }) {
  const { can } = useAuth();
  const location = useLocation();
  return can(location.pathname) ? children : <NoAccess />;
}

function MainApp() {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const toggleSidebar = useCallback(() => setIsCollapsed((c) => !c), []);
  // A page drawn as an Opposition Preview slide (embedMode.js): no sidebar, no glow, the page alone.
  useEffect(() => { installEmbedBridge(() => document.querySelector('.main-content')); }, []);

  return (
      <Router>
        <div className="app-container">
          {!EMBED && (
            <>
              {/* Ambient background glow */}
              <div className="ambient-glow">
                <div className="glow-orb-1" />
                <div className="glow-orb-2" />
              </div>

              {/* Film grain overlay */}
              <div className="film-grain" />

              <Sidebar isCollapsed={isCollapsed} toggleSidebar={toggleSidebar} />
            </>
          )}
          <div className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}${EMBED ? ' opv-embed-main' : ''}`}>
            <AccessGate>
              <Suspense fallback={<PageLoading />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/data-entry" element={<DataEntry />} />
                <Route path="/training-session-information" element={<TrainingSessionInformation />} />
                <Route path="/match-notes" element={<MatchNotes />} />
                <Route path="/manual-data-entry" element={<ManualDataEntry />} />
                <Route path="/injury-illness" element={<InjuryIllness />} />
                <Route path="/permissions" element={<Permissions />} />
                <Route path="/data-pull-performance" element={<DataPullPerformance />} />
                <Route path="/data-pull-football" element={<DataPullFootball />} />
                <Route path="/tableau" element={<Tableau />} />
                <Route path="/scoreboard-creator" element={<ScoreboardCreator />} />
                <Route path="/video-size-reducer" element={<VideoSizeReducer />} />
                <Route path="/line-up-creator" element={<LineUpCreator />} />
                <Route path="/schedule" element={<Schedule />} />
                <Route path="/chat" element={<Chat />} />
                <Route path="/match-video-uploader" element={<MatchVideoUploader />} />
                <Route path="/storage-testing" element={<StorageTesting />} />
                <Route path="/day-to-day-summaries" element={<DayToDaySummaries />} />
                {/* Renamed pages keep their old address as a redirect: a bookmark, a link pasted
                    into a message, and a permission saved under the old id all outlive the code
                    that named it. The list lives in pageRoutes.js beside the one userCanAccess
                    reads, so a rename is recorded once and both follow. */}
                {PAGE_REDIRECTS.map(([from, to]) => (
                  <Route key={from} path={from} element={<Navigate to={to} replace />} />
                ))}
                {/* The old Daily Training Report is gone. Its addresses land on the current report — as
                    plain redirects, NOT as pageRoutes aliases, so a grant saved for the old page
                    does not quietly become access to this one. */}
                {['/daily-training-report-old', '/daily-training', '/daily-gps'].map((from) => (
                  <Route key={from} path={from} element={<Navigate to="/daily-training-report" replace />} />
                ))}
                <Route path="/squad-depth" element={<SquadDepth />} />
                <Route path="/opposition-physicals" element={<OppositionPhysicals />} />
                <Route path="/training-drill-calculator" element={<TrainingDrillCalculator />} />
                <Route path="/individual-player-load-tracking-report" element={<IndividualPlayerLoadTrackingReport />} />
                <Route path="/load-budget-chronic-load-overview" element={<LoadBudgetChronicLoadOverview />} />
                <Route path="/wellness-longitudinal-tracking" element={<WellnessLongitudinalTracking />} />
                <Route path="/wellness-lateness" element={<WellnessLateness />} />
                <Route path="/rehab" element={<Rehab />} />
                <Route path="/availability" element={<Availability />} />
                <Route path="/travel" element={<Travel />} />
                <Route path="/testing" element={<Testing />} />
                <Route path="/nutrition" element={<NutritionPage />} />
                <Route path="/conditioning" element={<Conditioning />} />
                <Route path="/match-performance" element={<MatchPerformance />} />
                <Route path="/congestion-management" element={<CongestionManagement />} />
                <Route path="/heart-rate" element={<HeartRateReport />} />
                <Route path="/game-model-review" element={<GameModelReview />} />
                <Route path="/idp" element={<IDP />} />
                <Route path="/game-model" element={<GameModel />} />
                <Route path="/live" element={<Live />} />
                <Route path="/tactical-xml" element={<TacticalXml />} />
                <Route path="/style-of-play" element={<StyleOfPlay />} />
                <Route path="/team-analysis" element={<TeamAnalysis />} />
                <Route path="/opposition-preview" element={<OppositionPreview />} />
                <Route path="/training-focus" element={<TrainingFocus />} />
                <Route path="/playground" element={<PlaygroundPage />} />
                <Route path="/set-piece-summary" element={<SetPieceSummary />} />
                <Route path="/set-pieces" element={<SetPieces />} />
                <Route path="/set-pieces-positioning" element={<SetPiecePositioning />} />
                <Route path="/metric-maker" element={<MetricMaker />} />
                <Route path="/metric-explorer" element={<MetricExplorer />} />
                {/* Pages that are now views of one (Metric Explorer, Team Analysis): their old
                    addresses open the view. */}
                {VIEW_REDIRECTS.map(([from, to]) => (
                  <Route key={from} path={from} element={<Navigate to={to} replace />} />
                ))}
                <Route path="/over-time-metric-explorer" element={<OverTimeMetricExplorer />} />
                <Route path="/game-model-similarity" element={<GameModelSimilarity />} />
                <Route path="/squad-rating" element={<SquadRating />} />
                <Route path="/squad-valuations" element={<SquadValuations />} />
                <Route path="/packing-zones" element={<PackingZones />} />
                <Route path="/movement" element={<Movement />} />
                <Route path="/exploiting-space" element={<ExploitingSpace />} />
                <Route path="/scouting-recruitment" element={<ScoutingRecruitment />} />
                <Route path="/second-balls" element={<SecondBalls />} />
                <Route path="/player-synergies" element={<PlayerSynergies />} />
                <Route path="/coaching-post-match-report" element={<CoachingPostMatchReport />} />
                <Route path="/goals-analysis" element={<GoalsAnalysis />} />
                <Route path="/ball-in-play" element={<BallInPlay />} />
              </Routes>
              </Suspense>
            </AccessGate>
          </div>
        </div>
      </Router>
  );
}

// Shows the login screen until the user signs in, then the full app.
function Gate() {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <MainApp /> : <Login />;
}

function App() {
  return (
    <MsalProvider instance={msalInstance}>
    <ThemeProvider>
      <AuthProvider>
        <Gate />
      </AuthProvider>
    </ThemeProvider>
    </MsalProvider>
  );
}

export default App;
